import { type ComponentPropsWithoutRef } from 'react';
import styled, { css } from 'styled-components';

import IconInfoCircleSvg from '../../assets/icons/icon-info-circle.svg?react';

import {
  BaseButton,
  BaseInput,
  BaseMaskedInput,
} from './base-primitives.styles';

type PersonalDataInputFieldBaseProps = {
  ariaLabel: string;
  dataUi?: string;
  disabled?: boolean;
  inputMode?: ComponentPropsWithoutRef<'input'>['inputMode'];
  invalidMessage?: string;
  isInvalid: boolean;
  label: string;
  mask?: string;
  onOpenHelp?: () => void;
  placeholder: string;
  showHelpTip?: boolean;
  tipAriaLabel?: string;
  value: string;
};

type PersonalDataInputFieldEditableProps = PersonalDataInputFieldBaseProps & {
  onValueChange: (value: string) => void;
  readOnly?: false;
};

type PersonalDataInputFieldReadOnlyProps = PersonalDataInputFieldBaseProps & {
  onValueChange?: (value: string) => void;
  readOnly: true;
};

type PersonalDataInputFieldProps =
  | PersonalDataInputFieldEditableProps
  | PersonalDataInputFieldReadOnlyProps;

const PersonalDataFieldGroup = styled.div.attrs<{ $dataUi: string }>(
  ({ $dataUi }) => ({
    'data-ui': $dataUi,
  }),
)`
  display: grid;
  gap: ${({ theme }) => theme.scale.px(6)};
`;

const FieldLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.scale.px(2)};
`;

const FieldLabel = styled.span`
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 600;
  line-height: 1.25;
`;

const fieldInputStyles = css<{ $isDisabled: boolean; $isInvalid: boolean }>`
  width: 100%;
  min-height: ${({ theme }) => theme.scale.px(56)};
  box-sizing: border-box;
  border-radius: ${({ theme }) => theme.scale.px(10)};
  border: ${({ theme }) => theme.scale.px(2)} solid
    ${({ $isInvalid, theme }) =>
      $isInvalid ? theme.colors.support.danger : 'transparent'};
  padding: ${({ theme }) => theme.scale.px(18)}
    ${({ theme }) => theme.scale.px(16)};
  background: ${({ theme }) => theme.colors.surface.subtle};
  color: ${({ $isDisabled, theme }) =>
    $isDisabled ? theme.colors.text.subdued : theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.scale.px(16)};
  font-weight: 400;
  line-height: 1.25;
  cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed' : 'text')};
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.75 : 1)};

  &:focus-visible {
    outline: ${({ $isInvalid, theme }) =>
      $isInvalid
        ? 'none'
        : `${theme.scale.px(2)} solid ${theme.colors.brand.primary}`};
    outline-offset: ${({ $isInvalid, theme }) =>
      $isInvalid ? theme.scale.px(0) : theme.scale.px(2)};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.border.emphasis};
  }
`;

const FieldInput = styled(BaseInput)<{
  $isDisabled: boolean;
  $isInvalid: boolean;
}>`
  ${fieldInputStyles};
`;

const MaskedFieldInput = styled(BaseMaskedInput)<{
  $isDisabled: boolean;
  $isInvalid: boolean;
}>`
  ${fieldInputStyles};
`;

const TipButton = styled(BaseButton)`
  width: ${({ theme }) => theme.scale.px(12)};
  height: ${({ theme }) => theme.scale.px(12)};
  box-sizing: border-box;
  display: grid;
  place-items: center;
`;

const TipIcon = styled(IconInfoCircleSvg).attrs({
  'aria-hidden': true,
  focusable: 'false',
})`
  width: 100%;
  height: 100%;
  display: block;
  color: ${({ theme }) => theme.colors.text.primary};

  & path {
    stroke: currentColor;
  }
`;

const ValidationMessage = styled.p`
  width: 100%;
  margin: 0;
  text-align: center;
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-style: normal;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.support.danger};
  opacity: 0.7;
`;

export function PersonalDataInputField({
  ariaLabel,
  dataUi = 'personal-data-input-field',
  disabled = false,
  inputMode,
  invalidMessage,
  isInvalid,
  label,
  mask,
  onOpenHelp,
  onValueChange,
  placeholder,
  readOnly = false,
  showHelpTip = false,
  tipAriaLabel,
  value,
}: PersonalDataInputFieldProps) {
  return (
    <PersonalDataFieldGroup $dataUi={dataUi}>
      <FieldLabelRow>
        <FieldLabel>{label}</FieldLabel>
        {showHelpTip && onOpenHelp && tipAriaLabel ? (
          <TipButton
            type="button"
            aria-label={tipAriaLabel}
            onClick={onOpenHelp}
          >
            <TipIcon />
          </TipButton>
        ) : null}
      </FieldLabelRow>

      {mask ? (
        <MaskedFieldInput
          aria-invalid={isInvalid || undefined}
          aria-readonly={readOnly || undefined}
          aria-label={ariaLabel}
          disabled={disabled}
          $isDisabled={disabled}
          $isInvalid={isInvalid}
          inputMode={inputMode}
          mask={mask}
          placeholder={placeholder}
          readOnly={readOnly}
          value={value}
          onAccept={nextValue => onValueChange?.(String(nextValue))}
        />
      ) : (
        <FieldInput
          aria-invalid={isInvalid || undefined}
          aria-readonly={readOnly || undefined}
          aria-label={ariaLabel}
          disabled={disabled}
          $isDisabled={disabled}
          $isInvalid={isInvalid}
          inputMode={inputMode}
          placeholder={placeholder}
          readOnly={readOnly}
          value={value}
          onChange={event => onValueChange?.(event.target.value)}
        />
      )}

      {isInvalid && invalidMessage ? (
        <ValidationMessage>{invalidMessage}</ValidationMessage>
      ) : null}
    </PersonalDataFieldGroup>
  );
}
